<script setup lang="ts">
import { computed, nextTick, ref, watch } from "vue"
import {
  CalendarIcon,
  ChatAlt2Icon as ContactIcon,
  ThumbDownIcon as ErrorIcon,
} from "@heroicons/vue/outline"
import {
  BaseAPI,
  BaseInput,
  HttpError,
  TextArea,
  TrailsResp,
  isHttpError,
  useAppFlasher,
  useBaseAPIPost,
} from "@xy-planning-network/trees"
import { CheckCircleIcon as SuccessIcon } from "@heroicons/vue/solid"
import { useCaptcha } from "@/composables/forms"
import logger from "@/composables/logger"
import { FindAnAdvisorProfile } from "@/types/profile"
import Modal from "../shared/Modal.vue"
import { router } from "@/routes"
import { useCalendly } from "@/composables/calendly"
import { outboundCapture } from "@/composables/advisor-profile"
import ActionPanelWell from "@/components/shared/ActionPanelWell.vue"
import { HTTP_INTERNAL_SERVER_ERROR } from "@/constants/http"

const props = defineProps<{
  profile: FindAnAdvisorProfile
}>()

const showModal = defineModel<boolean>({ required: true })

const { captchaReady, captchaExecute, captchaRequest } =
  useCaptcha("faaContact")
const {
  result,
  error,
  isLoading,
  execute: postContactForm,
} = useBaseAPIPost(router.FindAdvisor_Contact({ slug: props.profile.slug }), {
  withDelay: 500,
})

const formDefaults = {
  email: "",
  message: "",
  name: "",
  phone: "",
  preferredContact: "",
  slug: props.profile.slug,
}
const contactForm = ref({ ...formDefaults })

const messageCount = computed(() => {
  return contactForm.value.message.length
})

const submit = () => {
  captchaExecute()
    .then(() =>
      postContactForm({ ...contactForm.value, captcha: captchaRequest.value })
    )
    .then(() => {
      // reset the form
      contactForm.value = { ...formDefaults }
    })
    .catch((err: HttpError | Error) => {
      if (!isHttpError(err)) {
        logger.error(err)
        useAppFlasher.genericError()
        return
      }

      if (isHttpError(err) && err.status === HTTP_INTERNAL_SERVER_ERROR) {
        useAppFlasher.genericError()
        return
      }
    })
}

watch(showModal, (show) => {
  // if the modal is closed reset the result and error values
  // a little timeout let's the close animation complete
  if (show === false) {
    setTimeout(() => {
      result.value = null
      error.value = null
    }, 500)
  }

  // draw focus on the first input element when the modal is opened
  // BaseInput does not expose the internal input el so we must
  // query the input instead of using a TemplateRef
  if (show === true) {
    nextTick(() => {
      const nameInput = document.querySelector<HTMLInputElement>("#name")
      if (nameInput !== null) {
        nameInput.focus()
      }
    })
  }
})

// NOTE(spk): there is minimal event handling available with Calendly
// we can at best know if the required script and styles have been added
// to the page, but there does not appear to be a way to determine if a Calendly
// booking url is valid.  Under some circumstances the Calendly UI will show a helpful error.
//
// We avoid showing the CalendlyConnect panel until we know Calendly scripts are ready,
// but we are relying on members to do their own testing from this point on.
//
// Members can apply a booking url to any provider on their profile.  We send the user
// to that direct link whenever it's an provider other than calendly.
// https://regex101.com/r/SOp2xx/1
const calendlyRegex = /^(?:https|http):\/\/(?:www\.)?calendly\.com\/[^ ]+$/
const hasCalendly = calendlyRegex.test(props.profile.bookingUrl)

const { openPopup, onScheduled, isLoaded } = useCalendly({
  url: props.profile.bookingUrl,
  utmMedium: "Find an Advisor Profile Page",
})

const openScheduleMeeting = () => {
  if (hasCalendly && isLoaded.value) {
    openPopup()
  } else {
    outboundCapture(props.profile.bookingUrl, true)
  }

  showModal.value = false
}

onScheduled((url: string) => {
  BaseAPI.post<TrailsResp<never>>(router.PortalEvents(), {
    event: "faa_calendly_scheduled",
    data: {
      link: url,
      slug: props.profile.slug,
    },
  }).catch(() => {
    // NOTE(spk): swallow errors - most errors are likely to be related to
    // browser interference and will just be noise.
  })
})
</script>

<template>
  <Modal
    v-if="error"
    key="error"
    v-model="showModal"
    :icon="{
      bgColor: 'bg-red-50',
      iconColor: 'text-red-400',
      icon: ErrorIcon,
    }"
    title="Whoops! Something unexpected happened"
  >
    <div>
      <p class="text-lg leading-normal">
        This is unusual and we've probably already been alerted to it, but if
        you continue to see this message please
        <a
          href="mailto:membership@xyplanningnetwork.com?subject=Find an Advisor search error"
          class="xy-link"
          >let us know</a
        >.
      </p>
      <button class="xy-btn-secondary mt-8" @click="error = null">
        Try Again
      </button>
    </div>
  </Modal>

  <Modal
    v-else-if="result"
    key="success"
    v-model="showModal"
    :icon="{
      bgColor: 'bg-green-50',
      iconColor: 'text-green-400',
      icon: SuccessIcon,
    }"
    title="Success! Your message has been shipped"
  >
    <div>
      <p class="text-lg leading-normal">
        We've let this advisor know you're interested in connecting. Keep an eye
        out for their response to your preferred contact method.
      </p>
      <button class="xy-btn-secondary mt-8" @click="showModal = false">
        Close
      </button>
    </div>
  </Modal>

  <Modal
    v-else
    key="form"
    v-model="showModal"
    :icon="{
      bgColor: 'bg-blue-50',
      iconColor: 'text-blue-400',
      icon: ContactIcon,
    }"
    :title="`Connect with ${props.profile.name}`"
  >
    <div>
      <div
        v-if="profile.bookingUrl"
        class="border-b border-b-neutral-100 pb-8 mb-8"
      >
        <ActionPanelWell
          btn-text="Select a Date & Time &rarr;"
          :icon="CalendarIcon"
          title="Let's Get to Know Each Other"
          content="Schedule a call with this advisor."
          @click="openScheduleMeeting"
        />
      </div>
      <h4 class="text-base leading-normal font-semibold mb-4">
        Request a Callback
      </h4>
      <form @submit.prevent="submit">
        <div class="grid gap-y-2.5">
          <div>
            <BaseInput
              id="name"
              v-model="contactForm.name"
              type="text"
              label="Name"
              placeholder="Full Name"
              required
            />
          </div>
          <div>
            <BaseInput
              v-model="contactForm.email"
              type="email"
              label="Email Address"
              placeholder="you@example.com"
              required
            />
          </div>
          <div class="">
            <BaseInput
              v-model="contactForm.phone"
              type="tel"
              label="Phone Number"
              placeholder="555-555-5555"
              required
            />
          </div>
          <div class="">
            <div class="my-2">
              <Radio
                v-model="contactForm.preferredContact"
                :columns="2"
                label="Preferred Contact Method"
                :options="[
                  { label: 'Email', value: 'email' },
                  { label: 'Phone', value: 'phone' },
                ]"
                required
              />
            </div>
          </div>
          <div class="sm:col-span-1">
            <TextArea
              v-model="contactForm.message"
              placeholder="Include details that can help your potential advisor make the most of your time together when they first contact you."
              label="What do you want this advisor to know?"
              maxlength="800"
              rows="6"
              required
            />
            <div class="mt-0.5 text-sm font-medium text-neutral-600">
              {{ messageCount }}/800
            </div>
          </div>
        </div>

        <div>
          <button
            class="xy-btn mt-8"
            type="submit"
            :disabled="!captchaReady || isLoading"
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  </Modal>
</template>
